import { motion } from 'framer-motion';
import React from 'react';

import { useCurrentNode } from '../store/hooks';

import { ConditionalNodeEditor } from './narrative-editor/ConditionalNodeEditor';
import { EndingNodeEditor } from './narrative-editor/EndingNodeEditor';
import { NodeHeader } from './narrative-editor/NodeHeader';
import { StandardNodeEditor } from './narrative-editor/StandardNodeEditor';

export const NarrativeEditor = React.memo(function NarrativeEditor() {
  const currentNode = useCurrentNode();

  if (!currentNode) {
    return (
      <div className="p-6 text-gray-500">
        <p>No node selected</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Unified Header Banner */}
      <NodeHeader
        title={currentNode.idAlias || currentNode.id.replace(/_/g, ' ')}
        label={currentNode.label}
        guidanceText={currentNode.guidance}
      />

      {/* Tip above editor */}
      <div className="mb-4 flex justify-center">
        <div className="bg-[#1a2d4d]/80 backdrop-blur-sm border border-[#4f46e5]/30 rounded-lg px-3 py-1.5 shadow-lg">
          <p className="text-xs text-gray-300">
            <span className="text-[#06b6d4] font-semibold">Tip:</span>{' '}
            {`Write in these boxes to create your node's text and choices`}
          </p>
        </div>
      </div>

      {/* Main Editor Card */}
      <motion.div
        className="bg-gradient-to-br from-[#0a0a0a] via-[#0c1222] to-[#080808] border border-[#1a2d4d] rounded-xl p-5 hover:border-[#4f46e5] hover:scale-[1.01] transition-all duration-300 shadow-[0_8px_32px_rgba(0,0,0,0.3),0_0_40px_rgba(79,70,229,0.1)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.4),0_0_60px_rgba(79,70,229,0.2)] flex-1 flex flex-col relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.6 }}
      >
        {/* Background Blur Orbs - Optimized */}
        <div
          className="absolute top-1/4 left-1/4 w-40 h-40 bg-[#4f46e5] opacity-5 rounded-full blur-[120px] animate-pulse"
          style={{ willChange: 'opacity' }}
        />
        <div
          className="absolute bottom-1/3 right-1/3 w-32 h-32 bg-[#06b6d4] opacity-8 rounded-full blur-[100px] animate-pulse"
          style={{ animationDelay: '1s', willChange: 'opacity' }}
        />
        <div
          className="absolute top-1/2 right-1/4 w-24 h-24 bg-[#8b5cf6] opacity-6 rounded-full blur-[80px] animate-pulse"
          style={{ animationDelay: '2s', willChange: 'opacity' }}
        />
        <div className="flex-1 flex flex-col max-w-3xl mx-auto w-full overflow-hidden relative z-10">
          {/* Node Content Editor - Main Text Scrolls Only */}
          <motion.div
            className="flex-1 flex flex-col overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="h-full">
              {/* Standard Node Editor */}
              {currentNode.type === 'Standard' && <StandardNodeEditor />}

              {/* Conditional Node Editor */}
              {currentNode.type === 'Conditional' && <ConditionalNodeEditor />}

              {/* Ending Node Editor */}
              {currentNode.type === 'Ending' && <EndingNodeEditor />}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
});
